import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "lib-grid-footer",
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: "./grid-footer.component.html",
  styleUrl: "./grid-footer.component.scss",
})
export class GridFooterComponent {
  private static nextInstanceId = 0;
  /** Unique per rendered instance so the page-size `<select>`/`<label>` `id`/`for` pair doesn't collide when the grid (and this footer) renders more than once on a page. */
  readonly pageSizeSelectId = `cdw-grid-page-size-${GridFooterComponent.nextInstanceId++}`;

  @Input() selectedCount = 0;
  @Input() showSelectedCount = true;

  /**
   * When true, render the "Rows per page" selector on the left of the footer
   * overlay. Independent of `selectedCount` — the selector stays visible even
   * when nothing is selected (the selection strip self-hides). Defaults to
   * false so existing consumers that don't wire it see no change.
   */
  @Input() showPageSizeSelector = false;
  /** Currently-selected page size; drives the `<select>`'s highlighted option. */
  @Input() pageSize = 25;
  /** Options offered in the page-size dropdown (EDUS-2713: 25/50/100/200/500). */
  @Input() pageSizeOptions: number[] = [25, 50, 100, 200, 500];
  /** Emits the newly-chosen page size when the user picks a different option. */
  @Output() pageSizeChange = new EventEmitter<number>();
  @Input() footerBgColor?: string; // Optional custom background color for footer badge
  @Input() itemLabel = 'item'; // Label for selected items (e.g., 'device', 'user')
  @Input() showingSelectedOnly = false; // Whether grid is currently filtered to show only selected items
  /**
   * Count of selected rows on the current page only. Used to derive
   * `showAcrossPagesHint`: when the global `selectedCount` exceeds the
   * per-page count, the user has cross-page selection and the footer
   * surfaces a small "(across pages)" disclosure so the user knows
   * "Clear selection" is global before clicking it. Defaults to the
   * global `selectedCount` (i.e., assume single-page) so consumers that
   * don't wire this Input never see the hint — backward compatible.
   */
  @Input() selectedOnPageCount: number | null = null;

  /**
   * When true, dim the actionable links ("Show Selected Only" / "Clear
   * selection") and short-circuit their click handlers. The selected-count
   * text remains readable so users still see how many rows are selected
   * while the lock is active. Wired by CdwSsrmGridComponent from its
   * `currentLockActive` state (sourced from the consumer's
   * `selectionLockActive$` Input — typically toggled while a bulk action
   * job is running). Defaults to false so consumers that don't bind a
   * lock observable behave exactly as before.
   */
  @Input() locked = false;

  @Output() clickDeselect = new EventEmitter<void>();
  @Output() clickShowSelectedOnly = new EventEmitter<boolean>();

  /**
   * True when there are selections on pages other than the current one.
   * Hidden by default for consumers that don't bind `selectedOnPageCount`.
   */
  get showAcrossPagesHint(): boolean {
    return (
      this.selectedOnPageCount !== null &&
      this.selectedCount > this.selectedOnPageCount
    );
  }

  onDeselect(): void {
    if (this.locked) return;
    this.clickDeselect.emit();
  }

  onShowSelectedOnly(): void {
    if (this.locked) return;
    this.clickShowSelectedOnly.emit(!this.showingSelectedOnly);
  }

  onPageSizeChange(event: Event): void {
    const next = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(next) || next === this.pageSize) return;
    // Don't write `this.pageSize` here — it's an @Input; the parent is the
    // source of truth and echoes the new value back via its `[pageSize]`
    // binding once it accepts the change. Emitting only (not mutating
    // locally) means the `<select>`'s highlighted option reflects whatever
    // the parent actually applies, including a future parent that rejects
    // the change.
    this.pageSizeChange.emit(next);
  }
}
